import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { EventType, EVENT_TYPE_LABELS, CreateEventInput, StudentEvent, UpdateEventInput } from '../../types/calendar';
import { SubjectWithThreads } from '../../types/subjects';

interface AddEventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (input: CreateEventInput) => void;
  onUpdate?: (id: string, input: UpdateEventInput) => void;
  onDelete?: (id: string) => void;
  subjects: SubjectWithThreads[];
  editingEvent?: StudentEvent | null;
  defaultDate?: string;
}

const EVENT_TYPES: EventType[] = ['exam', 'test', 'assignment', 'personal', 'school'];

export const AddEventModal: React.FC<AddEventModalProps> = ({
  isOpen,
  onClose,
  onCreate,
  onUpdate,
  onDelete,
  subjects,
  editingEvent,
  defaultDate,
}) => {
  const [title, setTitle] = useState('');
  const [eventType, setEventType] = useState<EventType>('exam');
  const [eventDate, setEventDate] = useState('');
  const [description, setDescription] = useState('');
  const [subjectId, setSubjectId] = useState('');

  useEffect(() => {
    if (editingEvent) {
      setTitle(editingEvent.title);
      setEventType(editingEvent.event_type as EventType);
      setEventDate(editingEvent.event_date.slice(0, 16));
      setDescription(editingEvent.description || '');
      setSubjectId(editingEvent.subject_id || '');
    } else {
      setTitle('');
      setEventType('exam');
      setEventDate(defaultDate || '');
      setDescription('');
      setSubjectId('');
    }
  }, [editingEvent, isOpen, defaultDate]);

  if (!isOpen) return null;

  const canSave = title.trim() && eventDate;

  const handleSave = () => {
    if (!canSave) return;
    const payload = {
      title: title.trim(),
      event_type: eventType,
      event_date: new Date(eventDate).toISOString(),
      description: description.trim() || undefined,
      subject_id: subjectId || undefined,
    };

    if (editingEvent && onUpdate) {
      onUpdate(editingEvent.id, payload);
    } else {
      onCreate(payload);
    }
    onClose();
  };

  const handleDelete = () => {
    if (editingEvent && onDelete) {
      onDelete(editingEvent.id);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[var(--bg-primary)] border border-[var(--border-color)] rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border-color)]">
          <h3 className="text-base font-semibold text-[var(--text-primary)]">
            {editingEvent ? 'Edit Event' : 'Add Event'}
          </h3>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors text-[var(--text-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-5 space-y-4">
          {/* Title */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Title</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g., Biology Mid-Term Exam"
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/50"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Type</label>
            <div className="flex flex-wrap gap-1.5">
              {EVENT_TYPES.map((type) => (
                <button
                  key={type}
                  onClick={() => setEventType(type)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all border ${
                    eventType === type
                      ? 'border-[var(--accent-primary)] bg-[var(--accent-primary)]/10 text-[var(--text-primary)]'
                      : 'border-[var(--border-color)] bg-[var(--surface-elevated)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)]'
                  }`}
                >
                  {EVENT_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </div>

          {/* Date */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Date & Time</label>
            <input
              type="datetime-local"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]/50"
            />
          </div>

          {/* Subject (optional) */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Subject (optional)</label>
            <select
              value={subjectId}
              onChange={(e) => setSubjectId(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] focus:outline-none focus:border-[var(--accent-primary)]/50"
            >
              <option value="">None</option>
              {subjects.map((s) => (
                <option key={s.subject_id} value={s.subject_id}>{s.name}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Notes (optional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Any extra notes..."
              rows={2}
              className="w-full px-3 py-2.5 rounded-lg bg-[var(--surface-elevated)] border border-[var(--border-color)] text-sm text-[var(--text-primary)] placeholder-[var(--text-tertiary)] focus:outline-none focus:border-[var(--accent-primary)]/50 resize-none"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-4 border-t border-[var(--border-color)]">
          <div>
            {editingEvent && onDelete && (
              <button
                onClick={handleDelete}
                className="px-3 py-2 rounded-lg text-xs font-medium text-red-400 hover:bg-red-500/10 transition-colors"
              >
                Delete
              </button>
            )}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!canSave}
              className={`px-4 py-2 rounded-lg text-xs font-medium transition-all ${
                canSave
                  ? 'bg-[var(--accent-primary)] text-white hover:opacity-90'
                  : 'bg-[var(--bg-tertiary)] text-[var(--text-tertiary)] cursor-not-allowed'
              }`}
            >
              {editingEvent ? 'Save Changes' : 'Add Event'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
